import React, { useCallback, useEffect, useState } from 'react';
import { adminService } from '../../api';
import './AdminUsefulLinksPage.css';

const CATEGORY_OPTIONS = [
    { value: 0, label: 'Ремонт и отделка' },
    { value: 1, label: 'Агентства недвижимости' },
];

const emptyForm = { category: 0, name: '', url: '', description: '', logoUrl: '', order: 0, isActive: true };

const AdminUsefulLinksPage = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [busyId, setBusyId] = useState(null);

    const [editingId, setEditingId] = useState(null); // null = list, 'new' = create, id = edit
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [formError, setFormError] = useState(null);

    const load = useCallback(() => {
        setLoading(true);
        setError(null);
        adminService.getUsefulLinks()
            .then(({ data }) => setItems(data))
            .catch(() => setError('Не удалось загрузить список.'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    const startCreate = () => {
        setForm(emptyForm);
        setFormError(null);
        setEditingId('new');
    };

    const startEdit = (link) => {
        setForm({
            category: link.category,
            name: link.name,
            url: link.url,
            description: link.description || '',
            logoUrl: link.logoUrl || '',
            order: link.order,
            isActive: link.isActive,
        });
        setFormError(null);
        setEditingId(link.id);
    };

    const cancelEdit = () => {
        setEditingId(null);
        setFormError(null);
    };

    const handleLogoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const { data } = await adminService.uploadUsefulLinkLogo(file);
            setForm(f => ({ ...f, logoUrl: data.url }));
        } catch {
            setFormError('Не удалось загрузить логотип.');
        } finally {
            setUploading(false);
        }
    };

    const save = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.url.trim()) {
            setFormError('Заполните название и ссылку.');
            return;
        }
        setSaving(true);
        setFormError(null);
        try {
            const payload = {
                category: Number(form.category),
                name: form.name.trim(),
                url: form.url.trim(),
                description: form.description.trim() || null,
                logoUrl: form.logoUrl || null,
                order: Number(form.order) || 0,
                isActive: form.isActive,
            };
            if (editingId === 'new') {
                await adminService.createUsefulLink(payload);
            } else {
                await adminService.updateUsefulLink(editingId, payload);
            }
            setEditingId(null);
            load();
        } catch {
            setFormError('Не удалось сохранить.');
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id) => {
        if (!window.confirm('Удалить ссылку?')) return;
        setBusyId(id);
        try {
            await adminService.deleteUsefulLink(id);
            setItems(prev => prev.filter(l => l.id !== id));
        } catch {
            alert('Не удалось удалить.');
        } finally {
            setBusyId(null);
        }
    };

    if (editingId !== null) {
        return (
            <div className="admin-links">
                <h1 className="admin-links__title">
                    {editingId === 'new' ? 'Новая ссылка' : 'Редактирование ссылки'}
                </h1>

                {formError && <div className="admin-links__error">{formError}</div>}

                <form className="admin-links__form" onSubmit={save}>
                    <label className="admin-links__label">
                        Категория
                        <select
                            className="admin-links__input"
                            value={form.category}
                            onChange={e => setForm(f => ({ ...f, category: Number(e.target.value) }))}
                        >
                            {CATEGORY_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </label>

                    <label className="admin-links__label">
                        Название компании
                        <input
                            className="admin-links__input"
                            value={form.name}
                            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                        />
                    </label>

                    <label className="admin-links__label">
                        Ссылка
                        <input
                            className="admin-links__input"
                            value={form.url}
                            onChange={e => setForm(f => ({ ...f, url: e.target.value }))}
                            placeholder="https://..."
                        />
                    </label>

                    <label className="admin-links__label">
                        Описание
                        <textarea
                            className="admin-links__textarea"
                            value={form.description}
                            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                        />
                    </label>

                    <div className="admin-links__label">
                        Логотип
                        <div className="admin-links__logo-row">
                            {form.logoUrl && <img src={form.logoUrl} alt="" className="admin-links__logo-preview" />}
                            <input type="file" accept="image/*" onChange={handleLogoUpload} disabled={uploading} />
                        </div>
                    </div>

                    <label className="admin-links__label">
                        Порядок сортировки
                        <input
                            type="number"
                            className="admin-links__input admin-links__input--narrow"
                            value={form.order}
                            onChange={e => setForm(f => ({ ...f, order: e.target.value }))}
                        />
                    </label>

                    <label className="admin-links__checkbox">
                        <input
                            type="checkbox"
                            checked={form.isActive}
                            onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))}
                        />
                        Активна (показывается на сайте)
                    </label>

                    <div className="admin-links__form-actions">
                        <button type="submit" className="admin-links__btn-save" disabled={saving}>
                            {saving ? 'Сохранение...' : 'Сохранить'}
                        </button>
                        <button type="button" className="admin-links__btn-cancel" onClick={cancelEdit}>
                            Отмена
                        </button>
                    </div>
                </form>
            </div>
        );
    }

    return (
        <div className="admin-links">
            <div className="admin-links__header">
                <h1 className="admin-links__title">Полезные ссылки</h1>
                <button className="admin-links__btn-add" onClick={startCreate}>+ Добавить</button>
            </div>

            {loading && <div className="admin-links__placeholder">Загрузка...</div>}
            {error && <div className="admin-links__placeholder">{error}</div>}
            {!loading && !error && items.length === 0 && (
                <div className="admin-links__placeholder">Ссылок пока нет.</div>
            )}

            <div className="admin-links__list">
                {items.map(l => (
                    <div key={l.id} className="admin-links__card">
                        <div className="admin-links__card-main">
                            <div className="admin-links__card-name">{l.name}</div>
                            <div className="admin-links__meta">
                                {CATEGORY_OPTIONS.find(o => o.value === l.category)?.label}
                                {' · '}
                                <span className={l.isActive ? 'admin-links__status--active' : 'admin-links__status--inactive'}>
                                    {l.isActive ? 'активна' : 'скрыта'}
                                </span>
                            </div>
                        </div>
                        <div className="admin-links__card-actions">
                            <button className="admin-links__btn-edit" onClick={() => startEdit(l)}>Изменить</button>
                            <button
                                className="admin-links__btn-delete"
                                disabled={busyId === l.id}
                                onClick={() => remove(l.id)}
                            >
                                Удалить
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default AdminUsefulLinksPage;
